import { Button } from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import { environment } from "@web-src/environments/environment";
import React from "react";
import { SetupMethodOption } from "./components/SetupMethodOption";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import { ReactComponent as GlobeIcon } from "./assets/globe.svg";
import { ReactComponent as GoogleIcon } from "./assets/google.svg";

export type SetupMethod = "forwarding" | "gmail";

const isGmailAuthAvailable = environment.featureFlags.isGmailAuthEnabled;

export const SelectSetupMethodStep: React.FC<{
  setupMethod: SetupMethod;
  onSetupMethodChange: (method: SetupMethod) => void;
  onContinue: () => void;
  isLoading?: boolean;
}> = ({ setupMethod, onSetupMethodChange, onContinue, isLoading = false }) => {
  const { t } = useTranslations();

  return (
    <div className="flex max-w-[900px] flex-col gap-10 max-lg:gap-6">
      <div className="flex gap-4">
        <div className="w-px shrink-0 self-stretch rounded-[1px] bg-[#E0E0E0]" />
        <span className="font-secondary text-lg font-medium text-dark">
          {t({
            id: "client-support-page.select-setup-method",
            defaultMessage: "Select setup method",
          })}
        </span>
      </div>

      <div
        className={cx(
          "grid gap-6 max-lg:grid-cols-1 max-lg:gap-3",
          isGmailAuthAvailable ? "grid-cols-2" : "grid-cols-1"
        )}
      >
        <SetupMethodOption
          icon={GlobeIcon}
          isSelected={setupMethod === "forwarding"}
          onSelect={() => onSetupMethodChange("forwarding")}
          title={
            <>
              <span>
                {t({
                  id: "client-support-page.email-forwarding",
                  defaultMessage: "Email forwarding",
                })}
              </span>
              <span className="text-sm font-normal text-primary-800">
                {t({
                  id: "client-support-page.recommended",
                  defaultMessage: "(recommended)",
                })}
              </span>
            </>
          }
          description={
            <ul className="list-disc pl-5">
              <li>
                {t({
                  id: "client-support-page.email-forwarding-custom-inbox-email",
                  defaultMessage: "You will get a custom Jugl Inbox email",
                })}
              </li>
              <li>
                {t({
                  id: "client-support-page.email-forwarding-forward-to-inbox",
                  defaultMessage:
                    "Forward your emails to your Jugl Inbox so that AI Agent or your team can handle it in one place",
                })}
              </li>
              <li>
                {t({
                  id: "client-support-page.email-forwarding-support-any-provider",
                  defaultMessage:
                    "Support any provider (Gmail, Outlook, Zoho, custom domain, etc.)",
                })}
              </li>
              <li>
                {t({
                  id: "client-support-page.email-forwarding-domain-verification",
                  defaultMessage: "Domain verification available (optional)",
                })}
              </li>
            </ul>
          }
        />
        {isGmailAuthAvailable && (
          <SetupMethodOption
            icon={GoogleIcon}
            isSelected={setupMethod === "gmail"}
            onSelect={() => onSetupMethodChange("gmail")}
            title={t({
              id: "client-support-page.gmail-authentication",
              defaultMessage: "Gmail Authentication",
            })}
            description={
              <ul className="list-disc pl-5">
                <li>
                  {t({
                    id: "client-support-page.gmail-authentication-support-only-gmail",
                    defaultMessage: "Support only Gmail provider",
                  })}
                </li>
              </ul>
            }
          />
        )}
      </div>

      <Button
        variant="primary"
        iconEnd={<ArrowRightIcon />}
        onClick={onContinue}
        isDisabled={isLoading}
        className="w-[280px] max-lg:w-full"
      >
        {t({
          id: "common.continue",
          defaultMessage: "Continue",
        })}
      </Button>
    </div>
  );
};
